/**
 * OpenAI's Responses API (native `web_search`, also through Azure and the
 * gateway) emits inline citations as a marker wrapped in Private Use Area
 * delimiters:
 *
 *     \uE200cite\uE202turn1view1\uE201
 *
 * `cite` is the marker kind and `turn1view1` is the reference: browsing turn 1,
 * first page viewed (`search4` = 4th search result, `news2`, `fetch0`, …).
 * Several refs can share one block.
 *
 * Those delimiters are invisible in every renderer and get dropped by most text
 * pipelines (JSON round-trips, markdown sanitizers, terminal output), which
 * leaves the bare payload behind as literal prose: `citeturn1view1`. Either way
 * the marker is display noise: the usable data lives in the part's
 * `providerMetadata.<provider>.annotations`, which carries url, title and the
 * span the marker occupied.
 *
 * IMPORTANT: those annotation `start_index`/`end_index` offsets are computed on
 * the text WITH the delimiters still in it. Render the citations from the
 * annotations FIRST (walking them last-to-first so earlier offsets stay valid),
 * and only then strip. Stripping first makes every offset stale.
 */

/** A whole PUA-delimited block, `\uE200…\uE201`, plus the space before it. */
const DELIMITED_MARKER = /[ \t]*\uE200[^\uE201]*\uE201/g

/**
 * The same payload once the delimiters are already lost: a marker keyword glued
 * to one or more `turn<n><kind><n>` refs. Anchored on both the keyword and the
 * ref shape, because on degraded text there are no boundaries left to trust and
 * a looser pattern would start eating prose.
 */
const DEGRADED_MARKER =
    /[ \t]*(?:cite|filecite|videocite|audiocite|imagecite|screenshot)(?:turn\d+[a-z]+\d+)+/gi

/**
 * Orphan delimiters: a block truncated mid-stream, or one whose payload some
 * upstream pass already removed.
 */
const STRAY_DELIMITERS = /[\uE200-\uE20F]/g

/**
 * Removes provider citation markers from text meant for display, in all three
 * states they show up in: well-formed, delimiter-stripped, and truncated.
 */
export function stripProviderCitationMarkers(text: string): string {
    if (!text) {
        return text
    }
    return text
        .replace(DELIMITED_MARKER, '')
        .replace(DEGRADED_MARKER, '')
        .replace(STRAY_DELIMITERS, '')
}
